import { PlanType } from '@/types/plan';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import SectionTitleForm from './SectionTitleForm';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import { MAX_INPUT } from '@/config/baseYup';
import { RegisterAccountFormSchemaType } from '@/shared/schema/register-account';
import { useTranslation } from '@/config/i18n';
import { CalendarCustomForm } from '@/components/common/ui/calendar';
import InfinitySelect from '@/components/common/ui/select';
import { MAX_LENGTH } from '@/shared/constants';
import { TreeSelectCustomForm } from '@/components/common/ui/treeSelect';
import { GraduationType } from '@/types/graduation';
import ButtonCustom from '@/components/common/ui/button';
import { CheckBoxCustomForm } from '@/components/common/ui/checkbox';
import PlanItem from './PlanItem';
import { useState } from 'react';
import RegisterAccountPolicy from './RegisterAccountPolicy';
import RegisterAccountTermService from './RegisterAccountTermService';

type Props = {
  formMethods: UseFormReturn<RegisterAccountFormSchemaType>;
  onRegisterAccount: (data: any) => void;
  selectedPlan: PlanType | undefined;
  setSelectedPlan: (data: PlanType) => void;
  loadingGlobal: boolean;
  graduations: GraduationType[];
  plans: PlanType[];
};

const RegisterAccountForm = ({
  formMethods,
  onRegisterAccount,
  selectedPlan,
  setSelectedPlan,
  loadingGlobal,
  graduations,
  plans,
}: Props) => {
  const [t] = useTranslation('');

  const { handleSubmit, setValue, watch } = formMethods;

  const values = watch();
  const [confirmPolicy, setConfirmPolicy] = useState(false);
  const [isReadPolicy, setIsReadPolicy] = useState(false);
  const [isReadTerm, setIsReadTerm] = useState(false);

  const isFilled = Object.values(values).every((value) => {
    return value !== undefined && value !== null && value !== '';
  });
  return (
    <FormProvider {...formMethods}>
      <form
        className="mt-[16px]"
        autoComplete="off"
        onSubmit={handleSubmit(onRegisterAccount)}
      >
        <SectionTitleForm
          title={t('register_account.personal_section.title')}
        />
        <div className="form-item flex flex-col gap-[6px] mt-6">
          <div className="label flex">
            <span>{t('register_account.personal_section.full_name_kanji')}</span>
            <span className="text-error-500">*</span>
          </div>
          <div className="flex flex-col md:flex-row justify-between gap-4 md:gap-0">
            <InputTextCustomForm
              name="last_name_kanji"
              wrapClass="w-full md:w-[calc(50%-8px)]"
              placeholder={t(
                'register_account.personal_section.last_name_kanji_placeholder',
              )}
              maxLength={MAX_INPUT}
            />
            <InputTextCustomForm
              name="first_name_kanji"
              wrapClass="w-full md:w-[calc(50%-8px)]"
              placeholder={t(
                'register_account.personal_section.first_name_kanji_placeholder',
              )}
              maxLength={MAX_INPUT}
            />
          </div>
        </div>

        <div className="form-item flex flex-col gap-[6px] mt-6">
          <div className="label flex">
            <span>
              {t('register_account.personal_section.full_name')}
            </span>
            <span className="text-error-500">*</span>
          </div>
          <div className="flex flex-col md:flex-row justify-between gap-4 md:gap-0">
            <InputTextCustomForm
              name="last_name"
              wrapClass="w-full md:w-[calc(50%-8px)]"
              placeholder={t(
                'register_account.personal_section.last_name_placeholder',
              )}
              maxLength={MAX_INPUT}
            />
            <InputTextCustomForm
              name="first_name"
              wrapClass="w-full md:w-[calc(50%-8px)]"
              placeholder={t(
                'register_account.personal_section.first_name_placeholder',
              )}
              maxLength={MAX_INPUT}
            />
          </div>
        </div>

        <div className="form-item flex flex-col md:flex-row justify-between gap-4 md:gap-[6px] mt-6">
          <div className="flex flex-col w-full md:w-[calc(50%-8px)]">
            <div className="label flex">
              <span>
                {t('register_account.personal_section.date_of_birth')}
              </span>
              <span className="text-error-500">*</span>
            </div>
            <CalendarCustomForm
              wrapClass="w-full"
              name="date_of_birth"
              dateFormat="yy/mm/dd"
              maxDate={new Date()}
              showIcon
              icon={() => <i className="pi pi-calendar text-[18px]" />}
              placeholder="yyyy/mm/dd"
              panelClassName="calendar-date-of-birth"
              showButtonBar
            />
          </div>

          <div className="flex flex-col w-full md:w-[calc(50%-8px)]">
            <div className="label flex">
              <span>{t('register_account.personal_section.gender')}</span>
              <span className="text-error-500">*</span>
            </div>
            <InfinitySelect
              name="gender"
              placeholder={t(
                'register_account.personal_section.gender_placeholder',
              )}
              wrapClass="w-full"
              isClearable={false}
              defaultOption={[
                { value: 'male', label: t('common.gender.male') },
                { value: 'female', label: t('common.gender.female') },
                { value: 'other', label: t('common.gender.other') },
              ]}
            />
          </div>
        </div>

        <SectionTitleForm
          title={t('register_account.contact_section.title')}
          className="mt-6"
        />
        <div className="form-item flex flex-col md:flex-row justify-between gap-4 md:gap-[6px] mt-[24px]">
          <div className="flex flex-col w-full md:w-[calc(50%-8px)]">
            <div className="label flex">
              <span>{t('register_account.contact_section.email_address')}</span>
              <span className="text-error-500">*</span>
            </div>
            <InputTextCustomForm
              name="email"
              wrapClass="w-full"
              placeholder={t(
                'register_account.contact_section.email_address_placeholder',
              )}
              maxLength={MAX_INPUT}
            />
          </div>

          <div className="flex flex-col w-full md:w-[calc(50%-8px)]">
            <div className="label flex">
              <span>{t('register_account.contact_section.phone_number')}</span>
              <span className="text-error-500">*</span>
            </div>
            <InputTextCustomForm
              name="phone_number"
              wrapClass="w-full"
              placeholder={t(
                'register_account.contact_section.phone_number_placeholder',
              )}
              maxLength={MAX_LENGTH.PHONE_NUMBER}
              isNumber
            />
          </div>
        </div>

        <SectionTitleForm
          title={t('register_account.address_section.title')}
          className="mt-[24px]"
        />
        <div className="form-item flex flex-col md:flex-row justify-between gap-4 md:gap-[6px] mt-[24px]">
          <div className="flex flex-col w-full md:w-[calc(50%-8px)]">
            <div className="label flex">
              <span>{t('register_account.address_section.post_code')}</span>
              <span className="text-error-500">*</span>
            </div>
            <InputTextCustomForm
              name="post_code"
              wrapClass="w-full"
              placeholder={t(
                'register_account.address_section.post_code_placeholder',
              )}
              maxLength={MAX_LENGTH.POSTCODE}
            />
          </div>

          <div className="flex flex-col w-full md:w-[calc(50%-8px)]">
            <div className="label flex">
              <span>{t('register_account.address_section.address')}</span>
              <span className="text-error-500">*</span>
            </div>
            <InputTextCustomForm
              name="address"
              wrapClass="w-full"
              placeholder={t(
                'register_account.address_section.address_placeholder',
              )}
              maxLength={MAX_LENGTH.ADDRESS}
            />
          </div>
        </div>

        <SectionTitleForm
          title={t('register_account.service_section.title')}
          className="mt-[24px]"
        />
        <div className="form-item flex flex-col gap-[6px] mt-[24px]">
          <div className="label flex">
            <span>{t('register_account.service_section.graduation')}</span>
            <span className="text-error-500">*</span>
          </div>
          <div className="flex justify-between">
            <TreeSelectCustomForm
              name="graduation_term_id"
              wrapClass="w-full"
              panelClassName="tree-hide-header tree-disabled-unselect"
              placeholder={t(
                'register_account.service_section.graduation_placeholder',
              )}
              optionsRender={graduations
                .filter((g) => g.is_visible !== false)
                .flatMap((g) =>
                  (g.terms ?? [])
                    .slice()
                    .sort(
                      (a, b) =>
                        (a.sort_order ?? 0) - (b.sort_order ?? 0),
                    )
                    .map((term) => ({
                      label: `${g.period_number}期ー${term.name || ''}`,
                      key: term.id,
                    })),
                )}
              isUsingOptionsRender
              panelStyle={{ width: '100%', maxWidth: '460px' }}
            />
          </div>
          <p className="text-[#757575] text-[12px] mt-2 whitespace-pre-line">
            {t('register_account.service_section.graduation_memo')}
          </p>
        </div>

        <div className="flex flex-col gap-4 mt-6">
          {plans.map((plan) => (
            <PlanItem
              plan={plan}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              setValue={setValue}
            />
          ))}
        </div>

        <SectionTitleForm
          title="利用規約及びプライバシーポリシー"
          className="mt-[24px]"
        />
        <div className="form-item flex flex-col gap-[6px] mt-[24px]">
          <div className="label flex">
            <span>{t('register_account.term_of_use_section.term_of_use')}</span>
          </div>
        </div>
        <RegisterAccountTermService isRead={isReadTerm} setIsRead={setIsReadTerm} />

        <div className="form-item flex flex-col gap-[6px] mt-[24px]">
          <div className="label flex">
            <span>{t('register_account.term_of_use_section.privacy_policy')}</span>
          </div>
        </div>
        <RegisterAccountPolicy isRead={isReadPolicy} setIsRead={setIsReadPolicy}  />

        <CheckBoxCustomForm
          onChange={(e) => setConfirmPolicy(e.checked)}
          checked={confirmPolicy}
          inputId=""
          name=""
          wrapClass="checkbox-sm mt-6"
          label="同意する"
          disabled={!isReadPolicy || !isReadTerm}
        />

        <div className="flex justify-center mt-[40px]">
          <ButtonCustom
            className="w-full disabled:bg-gray-500"
            type="submit"
            disabled={!confirmPolicy || !isFilled}
            loading={loadingGlobal}
          >
            次のステップに進む
          </ButtonCustom>
        </div>
      </form>
    </FormProvider>
  );
};

export default RegisterAccountForm;
